/**
 * mad-video — DSGVO-aware video player
 *
 * Consent model (see js/consent.js):
 *   - Server decides if a video needs consent at all (data-consent-required).
 *     First-party assets (same host / sibling subdomain) skip the overlay copy.
 *   - Remembered consent is a single cookie: mad_video_consent=1, 60 days.
 *
 * Thumbnails:
 *   - YouTube thumbnails are rendered server-side via the REST proxy so the
 *     browser never contacts img.youtube.com. The PHP layer sets the overlay
 *     background-image inline; JS does not fetch YouTube thumbs.
 *   - Vimeo still loads its thumbnail directly post-consent (unchanged).
 */

var consent = require('./js/consent');
var createPlyrPlayer = require('./js/plyr-player').createPlyrPlayer;
var MinimalPlayer = require('./js/minimal-player');
var slots = require('./js/slots');

class MadVideo {
  constructor(el) {
    this.el = el;
    this.overlay = el.querySelector('.mad-video__overlay');
    this.playerContainer = el.querySelector('.mad-video__player');
    this.playBtn = el.querySelector('.mad-video__play-btn');
    this.consentText = el.querySelector('.mad-video__consent-text');

    this.videoUrl = el.dataset.videoUrl || '';
    this.provider = el.dataset.videoProvider || 'auto';
    this.videoId = el.dataset.videoId || '';
    this.autoplay = el.dataset.autoplay === '1';
    this.loop = el.dataset.loop === '1';
    this.volume = parseFloat(el.dataset.volume || '50') / 100;
    this.matomoTitle = el.dataset.matomoTitle || '';
    this.matomoResource = el.dataset.matomoResource || '';
    // No overlay: the player is built right away and only starts on play / autoplay.
    // A missing attribute (older cached HTML) means the overlay is there.
    this.hasOverlay = el.dataset.overlay !== '0';
    this.playerConfig = parseJson(el.dataset.playerConfig);
    // PHP only renders the minimal style for MP4/HLS (never needs consent)
    this.isMinimal = el.classList.contains('mad-video--minimal');
    // Server is the source of truth. Fall back to client-side detection if
    // the attribute is missing (older cached HTML, unusual dynamic data).
    this.needsConsent = el.dataset.consentRequired != null
      ? el.dataset.consentRequired === '1'
      : this._clientSideNeedsConsent();

    this.isPlaying = false;
    this.consentGiven = false;
    this.plyr = null;
    this.minimal = null;

    this._init();
  }

  _init() {
    var type = this._getType();

    if (!this.needsConsent) {
      this.consentGiven = true;
    } else if (consent.hasConsent()) {
      this.consentGiven = true;
    } else if (consent.migrateLegacyConsent(type)) {
      this.consentGiven = true;
    } else if (window.madVideoConsentGiven && window.madVideoConsentGiven[type]) {
      this.consentGiven = true;
    }

    // External consent manager can unlock later.
    document.addEventListener('madVideoConsentGiven', function (e) {
      // Accept either a matching type or an omitted detail (broadcast all).
      if (!e.detail || !e.detail.type || e.detail.type === type) {
        if (this.consentGiven) return;
        this.consentGiven = true;
        this.el.classList.add('mad-video--consent-accepted');
        if (!this.isPlaying) {
          this._loadThumbnail();
        }
      }
    }.bind(this));

    // External consent manager can lock again (withdraw consent). Videos that
    // never needed consent keep playing — without an overlay nothing could restart them.
    document.addEventListener('madVideoConsentRevoked', function () {
      if (this.needsConsent) this._teardown();
    }.bind(this));

    if (this.consentGiven) {
      this.el.classList.add('mad-video--consent-accepted');
    }

    // Load thumbnail (Vimeo post-consent only — YouTube is handled by PHP).
    this._loadThumbnail();

    if (this.playBtn) {
      this.playBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        this._handlePlay();
      }.bind(this));
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        this._handlePlay();
      }.bind(this));
    }

    // Builder canvas: no player (no playback while editing). The minimal UI is still
    // slotted by setupMadVideo for styling; without overlay PHP shows the thumbnail.
    if (slots.isBuilder) return;

    if (this.isMinimal) {
      // With autoplay the overlay fades once the muted video actually plays
      if (!this.hasOverlay || this.autoplay) this._createMinimal(false);
      return;
    }

    if (!this.hasOverlay || (this.autoplay && this.consentGiven)) {
      this._play();
    }
  }

  _handlePlay() {
    if (this.isPlaying) return;

    if (!this.consentGiven) {
      consent.rememberConsent();
      this.consentGiven = true;
      this.el.classList.add('mad-video--consent-accepted');
      document.dispatchEvent(new CustomEvent('madVideoConsentGiven', {
        detail: { type: this._getType() },
        bubbles: true,
      }));
    }

    if (this.isMinimal) {
      this._playMinimalWithSound();
      return;
    }

    this._play();
  }

  // Overlay click: start with sound, synchronously inside the click handler (iOS)
  _playMinimalWithSound() {
    if (slots.isBuilder) return;

    if (this.minimal) {
      this.minimal.playWithSound(); // autoplay player that hasn't started (blocked or not in view yet)
    } else {
      this._createMinimal(true);
    }
    this._hideOverlay();
  }

  _createMinimal(startWithSound) {
    if (this.minimal) return;

    var self = this;
    // isPlaying stays false: an overlay click must still reach a not-yet-started autoplay player
    var create = function () {
      self.minimal = new MinimalPlayer(self.el, {
        url: self.videoUrl,
        provider: self._getType(),
        poster: self.el.dataset.thumbSrc || '',
        volume: self.volume,
        autoplay: self.autoplay,
        startWithSound: startWithSound,
        config: self.playerConfig,
        matomoTitle: self.matomoTitle,
        matomoResource: self.matomoResource,
        onPlaying: self._hideOverlay.bind(self),
        onError: self._resetPlay.bind(self),
      });
    };

    // hls.js (HLS sources) is a declared dependency of this script, so it has already run.
    create();
  }

  _play() {
    if (this.isPlaying || slots.isBuilder) return;
    this.isPlaying = true;

    // Plyr is a declared dependency of this script whenever a player on the page needs it.
    if (typeof window.Plyr !== 'undefined') {
      this._initPlyr();
    } else {
      console.warn('mad-video: Plyr is not available.');
      this.isPlaying = false;
    }
  }

  _initPlyr() {
    var type = this._getType();
    var embedId = '';

    if (type === 'youtube') {
      embedId = this.videoId || this._extractYoutubeId(this.videoUrl);
      if (!embedId) {
        console.error('mad-video: Cannot determine YouTube ID.');
        this._resetPlay();
        return;
      }
    } else if (type === 'vimeo') {
      embedId = this.videoId || this._extractVimeoId(this.videoUrl);
      if (!embedId) {
        console.error('mad-video: Cannot determine Vimeo ID.');
        this._resetPlay();
        return;
      }
    }

    // With an overlay the player is built on click (or autoplay) and starts right away.
    // Without one it is built on page load and waits for the viewer, downloading nothing.
    var waitForViewer = !this.hasOverlay && !this.autoplay;

    this.plyr = createPlyrPlayer(this.playerContainer, {
      type: type,
      url: this.videoUrl,
      embedId: embedId,
      autoplay: this.autoplay,
      startOnReady: !waitForViewer,
      lazy: waitForViewer,
      poster: this.hasOverlay ? '' : (this.el.dataset.thumbSrc || ''),
      loop: this.loop,
      volume: this.volume,
      config: this.playerConfig,
      matomoTitle: this.matomoTitle,
      matomoResource: this.matomoResource,
      onReady: this._hideOverlay.bind(this),
      onPlaying: this._hideOverlay.bind(this),
      onError: this._resetPlay.bind(this),
    });
  }

  _hideOverlay() {
    this.el.classList.add('mad-video--active');
  }

  // Destroy the player and return the element to its pre-consent state.
  _teardown() {
    var videoEl = this.playerContainer ? this.playerContainer.querySelector('video') : null;
    if (videoEl && videoEl._hlsInstance) {
      try { videoEl._hlsInstance.destroy(); } catch (e) {}
      videoEl._hlsInstance = null;
    }

    if (this.plyr) {
      try { this.plyr.destroy(); } catch (e) {}
      this.plyr = null;
    }

    if (this.playerContainer) this.playerContainer.innerHTML = '';

    this.isPlaying = false;
    this.consentGiven = !this.needsConsent;
    this.el.classList.remove('mad-video--active');
    if (this.needsConsent) {
      this.el.classList.remove('mad-video--consent-accepted');
    }
  }

  _resetPlay() {
    this.isPlaying = false;
    this.el.classList.remove('mad-video--active');
  }

  _loadThumbnail() {
    // YouTube thumbnails are rendered inline by PHP via the REST proxy.
    // Custom uploads also arrive inline from PHP. If we already have one, stop.
    if (this.overlay && this.overlay.style.backgroundImage) return;

    var type = this._getType();
    var self = this;

    if (this.overlay && this.el.dataset.thumbSrc) {
      this.overlay.style.backgroundImage = 'url("' + this.el.dataset.thumbSrc + '")';
      return;
    }

    // Vimeo: fetch thumbnail post-consent only. Pre-consent we leave the
    // overlay tinted; the user has not yet opted into any Vimeo contact.
    if (type === 'vimeo' && this.consentGiven) {
      var vimeoId = this.videoId || this._extractVimeoId(this.videoUrl);
      if (!vimeoId) return;

      fetch('https://vimeo.com/api/v2/video/' + vimeoId + '.json')
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data[0] && data[0].thumbnail_large) {
            if (self.overlay) self.overlay.style.backgroundImage = 'url(' + data[0].thumbnail_large + ')';
          }
        })
        .catch(function (e) {
          console.warn('mad-video: Vimeo thumbnail fetch failed', e);
        });
    }
  }

  _getType() {
    if (this.provider && this.provider !== 'auto') return this.provider;
    if (this._extractVimeoId(this.videoUrl)) return 'vimeo';
    if (this._extractYoutubeId(this.videoUrl)) return 'youtube';
    if (/\.m3u8$/i.test(this.videoUrl)) return 'hls';
    return 'mp4';
  }

  // Fallback only — server-side is authoritative via data-consent-required.
  _clientSideNeedsConsent() {
    var type = this._getType();
    if (type !== 'youtube' && type !== 'vimeo') return false;
    return !this._isLocal();
  }

  _isLocal() {
    if (!this.videoUrl) return false;
    if (this.videoUrl.charAt(0) === '/') return true;
    try {
      var hostname = window.location.hostname;
      var parsed = new URL(this.videoUrl);
      var parts = hostname.split('.');
      var rootDomain = parts.slice(-2).join('.');
      return parsed.hostname === hostname || parsed.hostname.endsWith('.' + rootDomain);
    } catch (e) {
      return false;
    }
  }

  _extractYoutubeId(url) {
    if (!url) return null;
    var m = url.match(
      /(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:[^#]*&)?v=|embed\/|shorts\/|live\/|v\/|e\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i
    );
    return m ? m[1] : null;
  }

  _extractVimeoId(url) {
    if (!url) return null;
    var m = url.match(/(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/);
    return m ? m[1] : null;
  }
}

function parseJson(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    console.warn('mad-video: invalid data-player-config', e);
    return null;
  }
}

function setupMadVideo() {
  document.querySelectorAll('.mad-video').forEach(function (el) {
    if (!el._madVideoInstance) {
      el._madVideoInstance = new MadVideo(el);
    }
  });
  slots.applyAll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupMadVideo);
} else {
  setupMadVideo();
}

window.setupMadVideo = setupMadVideo;
window.madGrantVideoConsent = consent.madGrantVideoConsent;
window.madRevokeVideoConsent = consent.madRevokeVideoConsent;
